import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../Button/Button";
import {
  FeatureResource,
  FeatureValue,
  VehicleRequirement,
  FeatureMatch,
  featureGet,
  featurePut,
} from "../../api/vehicleFeatures";
import { FeatureValueInput, RequirementFields } from "./FeatureFields";

export type ResourceFeaturesHandle = { save: () => Promise<void> };
type ResourceFeaturesProps = {
  workspaceId: string;
  resourceId: string;
  kind: "vehicle" | "order";
  readOnly?: boolean;
  embedded?: boolean;
  disabled?: boolean;
};
export const ResourceFeatures = forwardRef<
  ResourceFeaturesHandle,
  ResourceFeaturesProps
>(function ResourceFeatures(
  {
    workspaceId,
    resourceId,
    kind,
    readOnly = false,
    embedded = false,
    disabled: parentDisabled = false,
  },
  ref,
) {
  const { t } = useTranslation("workspace");
  const [data, setData] = useState<FeatureResource>();
  const [values, setValues] = useState<Record<string, FeatureValue>>({});
  const [requirements, setRequirements] = useState<VehicleRequirement[]>([]);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [matches, setMatches] = useState<FeatureMatch[]>();
  const [matching, setMatching] = useState(false);
  // Invalidate requests when the resource or local requirements change. A late
  // assessment must never appear beneath a newer draft or a different order.
  const requestVersion = useRef(0);
  useEffect(() => {
    const version = ++requestVersion.current;
    setBusy(false);
    setMatching(false);
    setData(undefined);
    setMatches(undefined);
    featureGet<FeatureResource>(workspaceId, `${kind}/${resourceId}`)
      .then((d) => {
        if (version === requestVersion.current) {
          setData(d);
          setValues(d.values || {});
          setRequirements(d.requirements || []);
          setError(false);
        }
      })
      .catch(() => {
        if (version === requestVersion.current) setError(true);
      });
    return () => {
      requestVersion.current++;
    };
  }, [workspaceId, resourceId, kind, reload]);
  const changed =
    data &&
    JSON.stringify(kind === "vehicle" ? values : requirements) !==
      JSON.stringify(
        kind === "vehicle" ? data.values || {} : data.requirements || [],
      );
  const save = async () => {
    if (readOnly) return;
    if (!data || busy) {
      setError(true);
      throw new Error("Vehicle attributes are not ready");
    }
    if (!changed) return;
    const version = ++requestVersion.current;
    setMatching(false);
    setBusy(true);
    setError(false);
    setMatches(undefined);
    try {
      const saved = await featurePut<FeatureResource>(
        workspaceId,
        `${kind}/${resourceId}`,
        {
          expectedRevision: data.revision,
          schemaRevision: data.schema.revision,
          [kind === "vehicle" ? "values" : "requirements"]:
            kind === "vehicle" ? values : requirements,
        },
      );
      if (version === requestVersion.current) {
        setData(saved);
        setValues(saved.values || {});
        setRequirements(saved.requirements || []);
      }
    } catch (failure) {
      if (version === requestVersion.current) setError(true);
      throw failure;
    } finally {
      if (version === requestVersion.current) setBusy(false);
    }
  };
  useImperativeHandle(ref, () => ({ save }));
  const disabled = readOnly || parentDisabled || busy || !data?.editable;
  const visibleDefinitions =
    data?.schema.definitions.filter(
      (d) => !d.archived || values[d.id] !== undefined,
    ) || [];
  return (
    <section
      className={
        embedded ? "space-y-3" : "my-4 rounded-xl border border-border p-4"
      }
    >
      {!embedded && (
        <>
          <h3 className="text-base font-semibold text-text">
            {t(kind === "vehicle" ? "features.title" : "features.requirements")}
          </h3>
          <p className="text-sm text-secondary">
            {t(kind === "vehicle" ? "features.vehicleHelp" : "features.orderHelp")}
          </p>
        </>
      )}
      {!data && !error && (
        <p role="status" className="text-sm text-muted">
          {t("features.loading")}
        </p>
      )}
      {data &&
        (kind === "vehicle" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {visibleDefinitions.map((d) => (
              <div key={d.id}>
                <p className="mb-1 text-sm font-medium">
                  {d.name}
                  {d.unit ? ` (${d.unit})` : ""}
                  {d.archived ? ` · ${t("features.archived")}` : ""}
                </p>
                <FeatureValueInput
                  definition={d}
                  value={values[d.id]}
                  disabled={disabled || d.archived}
                  onChange={(v) =>
                    setValues((current) => {
                      const next = { ...current };
                      if (v === undefined) delete next[d.id];
                      else next[d.id] = v;
                      return next;
                    })
                  }
                />
              </div>
            ))}
          </div>
        ) : (
          <RequirementFields
            definitions={data.schema.definitions}
            value={requirements}
            onChange={(v) => {
              requestVersion.current++;
              setMatching(false);
              setMatches(undefined);
              setRequirements(v);
            }}
            disabled={disabled}
          />
        ))}
      {data && kind === "vehicle" && !visibleDefinitions.length && (
        <p role="status" className="text-sm text-secondary">
          {t(
            data.schema.definitions.length
              ? "features.allArchived"
              : "features.emptyVehicle",
          )}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {t("features.error")}{" "}
          <button
            type="button"
            disabled={busy}
            className="underline"
            onClick={() => setReload((n) => n + 1)}
          >
            {t("features.reload")}
          </button>
        </p>
      )}
      {!embedded && (
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {kind === "order" && data && (
            <Button
              type="button"
              variant="secondary"
              disabled={busy || matching || !!changed || !requirements.length}
              onClick={async () => {
                const version = ++requestVersion.current;
                setMatching(true);
                setError(false);
                setMatches(undefined);
                try {
                  const result = await featureGet<{
                    candidates: FeatureMatch[];
                    requirementsRevision: number;
                    schemaRevision: number;
                  }>(workspaceId, `order/${resourceId}/matches`);
                  if (version !== requestVersion.current) return;
                  if (
                    result.requirementsRevision !== data.revision ||
                    result.schemaRevision !== data.schema.revision
                  ) {
                    // Another editor changed the saved criteria. Keep the draft
                    // visible, but require explicit reload before presenting matches.
                    setError(true);
                    return;
                  }
                  setMatches(result.candidates);
                } catch {
                  if (version === requestVersion.current) setError(true);
                } finally {
                  if (version === requestVersion.current) setMatching(false);
                }
              }}
            >
              {t(matching ? "features.loading" : "features.check")}
            </Button>
          )}
          {!readOnly &&
            data?.editable &&
            (kind === "order" || visibleDefinitions.length > 0) && (
              <Button
                type="button"
                variant="primary"
                disabled={
                  busy ||
                  !changed ||
                  (kind === "order" && requirements.some((r) => r.value === ""))
                }
                onClick={() => {
                  void save().catch(() => {});
                }}
              >
                {t(
                  busy
                    ? "features.saving"
                    : kind === "vehicle"
                      ? "features.saveValues"
                      : "features.saveRequirements",
                )}
              </Button>
            )}
        </div>
      )}
      {matches && (
        <div className="mt-4 border-t border-border pt-4">
          <p className="mb-3 text-sm text-muted">{t("features.advisory")}</p>
          {!matches.length && (
            <p className="text-sm">{t("features.noCandidates")}</p>
          )}
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {matches.map((m) => (
              <details
                key={m.vehicleId}
                className="rounded-lg border border-border p-3"
              >
                <summary className="cursor-pointer text-sm">
                  <span className="font-medium">
                    {[m.name, m.registrationNumber]
                      .filter(Boolean)
                      .join(" · ") || m.vehicleId}
                  </span>
                  <span className="ml-2 text-muted">
                    {t(`features.${m.status}`)}
                  </span>
                </summary>
                <p className="mt-2 text-sm text-muted">
                  {t("features.drivers")}:{" "}
                  {m.drivers.map((d) => d.name).join(", ") ||
                    t("features.noDrivers")}
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {m.checks.map((c, i) => (
                    <li key={i}>
                      {c.name}: {t(`features.${c.operator}`)}{" "}
                      {c.expectedLabel ||
                        (typeof c.value === "boolean"
                          ? t(c.value ? "features.yes" : "features.no")
                          : String(c.value))}{" "}
                      {c.unit} · {t("features.actual")}:{" "}
                      {c.actual === null
                        ? t("features.unset")
                        : c.actualLabel ||
                          (typeof c.actual === "boolean"
                            ? t(c.actual ? "features.yes" : "features.no")
                            : String(c.actual))}{" "}
                      · {t(`features.${c.status}`)}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </div>
      )}
    </section>
  );
});
